import { useCallback, useEffect, useMemo, useRef } from 'react';
import useTextSearchReducer, {
  setByTextValue,
  clickedOut,
  inputChanged,
  inputFocused,
  toggleDropdown,
} from 'redux-implementations/textSearch';
import useMakesAndModels from 'lib/hooks/useMakesAndModels';
import TextSearchList from 'lib/TextSearchList';
import useHashParamFeatureFlag from 'lib/hooks/useHashParamFeatureFlag';
import { ListItem } from 'types/TextSearch';
import { TextSearchState } from 'types/State';
import { blurInput, handleKeyDown } from './helpers';
import { TextSearchValue } from '.';

export default function useTextSearch(
  toggleModal?: () => void,
  setInputText?: (text: string) => void,
  onChange?: (item: TextSearchValue) => void
): {
  state: TextSearchState;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFocus: () => void;
  handleClickOutside: () => void;
  onDropdownClick: (list: ListItem) => void;
  dispatch: React.Dispatch<any>;
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
} {
  const makesAndModels = useMakesAndModels();
  const textSearchList = useMemo(() => new TextSearchList(makesAndModels), [makesAndModels]);
  const multiMakeModelEnabled = useHashParamFeatureFlag('multiMakeModel');

  const [state, dispatch] = useTextSearchReducer(textSearchList);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = e.target;
      dispatch(inputChanged(value));
    },
    [dispatch]
  );

  const handleFocus = useCallback((): void => {
    dispatch(inputFocused);
    document.addEventListener('click', blurInput);
  }, [dispatch]);

  const handleClickOutside = useCallback((): void => {
    if (state.dropDownOpen) {
      dispatch(clickedOut);
    }
  }, [dispatch, state.dropDownOpen]);

  const handleSelection = useCallback(
    (item: ListItem): void => {
      if (multiMakeModelEnabled) {
        dispatch(setByTextValue(''));
      }

      if (item.make) {
        onChange?.({
          make: item.make,
          model: item.model,
        });
        return;
      }

      if (item.searchParams.body_type) {
        onChange?.({
          bodyType: item.searchParams.body_type,
        });
      }
    },
    [onChange, dispatch, multiMakeModelEnabled]
  );

  const onDropdownClick = useCallback(
    (item: ListItem): void => {
      dispatch(inputChanged(item.displayName));
      dispatch(toggleDropdown);
      setInputText?.(item.displayName);
      toggleModal?.();
      handleSelection(item);
    },
    [dispatch, setInputText, toggleModal, handleSelection]
  );

  const handleKeyboardSelection = useCallback((): void => {
    if (typeof state.cursor === 'number') {
      const item = state.list[state.cursor];
      handleSelection(item);
      toggleModal?.();
    }
  }, [state.cursor, state.list, handleSelection, toggleModal]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      handleKeyDown(e, state, dispatch, handleKeyboardSelection);
    },
    [state, dispatch, handleKeyboardSelection]
  );

  return {
    state,
    handleInputChange,
    handleFocus,
    handleClickOutside,
    onDropdownClick,
    dispatch,
    handleInputKeyDown,
  };
}

export function useOptimizedClickAway<T extends HTMLElement>(
  callback: () => void
): React.RefObject<T> {
  const ref = useRef<T>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    },
    [callback]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handleClickOutside]);

  return ref;
}

export function shouldComponentUpdate(
  prevProps: Record<string, any>,
  nextProps: Record<string, any>
): boolean {
  return (
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.toggleModal === nextProps.toggleModal &&
    prevProps.setInputText === nextProps.setInputText &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.value?.searchText === nextProps.value?.searchText &&
    prevProps.value?.make === nextProps.value?.make &&
    prevProps.value?.model === nextProps.value?.model &&
    prevProps.value?.bodyType === nextProps.value?.bodyType
  );
}
